import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import useAggregationData from '@/api/aggregation/use-aggregation-data';
import useCustomerData from '@/api/customer-data/use-customer-data';
import useUserContext from '@/api/user-context/use-user-context';
import useClientCommunication from '@/game-client/hooks/use-client-communication';
import useUpdateGameActivity from '@/game-client/hooks/use-update-game-activity';
import { useDetachedModeBoot } from '@/logic/use-detached-mode-boot';
import { useEmbeddedLobbySessionUpdate } from '@/logic/web-wrapper/use-embedded-lobby-session-update';
import useGameUidParam from '@/lostHooks/gameUid/useGameUid';
import { useServerTimeInitialization } from '@/features/Timer/useServerTimeInitialization';
import useAmplitudeExperiments from '@/utilities/logger/amplitude/experiments/useAmplitudeExperiments';
import { useAccountUm } from '@/unified-modules/account/useAccountUm';
import { useSessionKeepAlive } from '@/lostHooks/use-session-keepalive/useSessionKeepAlive';
import { useStore } from '@/store/useStore';
import { getBalanceData } from '@/unified-modules/wallet/helpers/get-balance-data';
import { setGameContext, setGameWindowContext } from '@/store/actions';
import getQueryParams from '@/utilities/getters/getQueryParams';
import { useDisplayQueuePolling } from './useDisplayQueuePolling';
import { useMdsPromoListener } from './useMdsPromoListener';
import { useRealityCheckAutoplayGuard } from './use-reality-check-autoplay-guard';
import { usePopupAutoplayGuard } from './use-popup-autoplay-guard';
import { usePokerKeepAlive } from './usePokerKeepAlive';
import { useConnectivityReload } from './useConnectivityReload';
import { useReloadBalanceOnVisible } from './useReloadBalanceOnVisible';
import { useLogGameLaunched } from './useLogGameLaunched';
import { initializeSnowplow } from '@/utilities/logger/snowplow/snowplow-client';
import { useDybPopupCounter } from './useDybPopupCounter';
import { useGameLifecycleTracking } from './useGameLifecycleTracking';
import { useHistoryGameSync } from './useHistoryGameSync';
import GameWindowDocumentTitle from './GameWindowDocumentTitle';
import { resolveLaunchGeoProduct } from '@/geolocation/resolve-launch-geo-product';

/**
 * Side effects for the home route: data/session bootstrap, auth, popup queue polling.
 * Document title updates live in {@link GameWindowDocumentTitle} to avoid re-rendering
 * this wrapper on every timer tick.
 */
const HomeRouteEffects = ({ children }: { children: React.ReactNode }) => {
  const hasActiveSession = useStore(
    (s) => s.userSessionContext.hasActiveSession,
  );

  const gameUid = useGameUidParam();
  const { gameUid: pathGameUid } = useParams();
  const gameLoadSuccess = useStore((s) => s.gameStatus.gameLoadSuccess);
  const runtimeProduct = useStore((s) => s.runtime.product);
  const isAndroid = useStore((s) => s.runtime.wrapperType.isAndroid);

  useEffect(() => {
    if (pathGameUid) {
      setGameContext({ isShortLinkLaunch: true });
    } else if (getQueryParams('gameUid') || getQueryParams('providerRef')) {
      setGameContext({ isShortLinkLaunch: false });
    }
  }, [pathGameUid]);

  useEffect(() => {
    setGameContext({
      launchGeoProduct: resolveLaunchGeoProduct({
        runtimeProduct,
        providerName: getQueryParams('providerName'),
        isAndroid,
      }),
    });
  }, [gameUid, isAndroid, pathGameUid, runtimeProduct]);

  useLogGameLaunched();
  useAmplitudeExperiments();
  useHistoryGameSync();
  useDetachedModeBoot(gameLoadSuccess);
  useEmbeddedLobbySessionUpdate();
  const { keepSessionAlive } = useAccountUm({ bootstrap: true });

  useSessionKeepAlive({
    onActivity: keepSessionAlive,
    enabled: hasActiveSession,
  });
  useClientCommunication();
  useUpdateGameActivity();

  useAggregationData(gameUid);

  useCustomerData(undefined);
  useDybPopupCounter();

  useUserContext();

  const { fireGameLaunch } = useGameLifecycleTracking();

  useEffect(() => {
    if (!gameLoadSuccess) {
      return;
    }

    void (async () => {
      await initializeSnowplow();
      fireGameLaunch();
    })();
  }, [gameLoadSuccess, fireGameLaunch]);

  useServerTimeInitialization();

  // Fetch the header balance as soon as the session is valid, not only when the
  // game window is opened.
  useEffect(() => {
    if (!hasActiveSession) return;

    getBalanceData((balance) => {
      setGameWindowContext({ balance });
    });
  }, [hasActiveSession]);

  useDisplayQueuePolling();
  useRealityCheckAutoplayGuard();
  usePopupAutoplayGuard();
  useMdsPromoListener();
  usePokerKeepAlive();
  useConnectivityReload();
  useReloadBalanceOnVisible();

  return (
    <>
      <GameWindowDocumentTitle />
      {children}
    </>
  );
};

export default HomeRouteEffects;
